import { ActivitiesData } from '../types';

export const activitiesData: ActivitiesData = {
  hero: {
    title: 'Build the Network, Collect PLAA Points',
    description: `You’re already contributing to the growth of the Protocol Labs Network. Complete the activities below to have your contributions recognized as PLAA points—a way to track and celebrate your participation in the community. Click any activity to view details and discover new ways to get involved.`,
    note: ''
  },
  activities: [
    // ─── Repeatable ───────────────────────────────────────────────────────────
    {
      id: 'contribute-forum-response',
      category: 'Knowledge Sharing',
      activity: 'Thoughtful Responder',
      networkValue: 'Write a thoughtful, substantive response to a forum post',
      points: '250',
      frequency: 'Repeatable',
      verificationType: 'Manual Review',
      cta: 'confirm',
      popupContent: {
        title: 'Thoughtful Responder',
        overview: 'Write a thoughtful, substantive response to an existing LabOS Forum discussion that moves the conversation forward. Strong responses should generally be geared towards creating the opportunity for further and deeper responses.',
        networkBenefits: 'Sharing knowledge and experience through thoughtful forum posts, office hours, and published resources is core to the knowledge sharing that builds a stronger PL Network.',
        rules: [
          'Responses must do at least one of the following: add meaningful insight, ask a sharp follow-up question, share relevant personal or professional experience, or introduce useful evidence, context, or interpretation',
          'Responses must be posted by a human user, not an agent',
          'Only responses posted within 7 days of the original thread will count',
          'Responses will be reviewed by the PLAA Working Group before points are awarded'
        ],
        links: [
          { text: 'LabOS Forum', url: '/forum?cid=0' }
        ]
      }
    },
    {
      id: 'quick-conversationalist',
      category: 'Knowledge Sharing',
      activity: 'Quick Conversationalist',
      networkValue: 'You were quick to respond to a forum post and kept the momentum going',
      points: '50',
      frequency: 'Repeatable',
      verificationType: 'Auto',
      cta: 'confirm',
      popupContent: {
        title: 'Quick Conversationalist',
        overview: 'You were quick to respond to a forum post. That keeps the conversation flowing and helps the LabOS forum feel more alive and dynamic.',
        networkBenefits: 'Keeps forum discussions active while the topic is timely and increases the chance that questions turn into useful exchanges.',
        rules: [
          'The qualifying response must be posted shortly after the original forum post while the discussion is still active',
          'The response must be substantive enough to keep the conversation moving'
        ],
        links: [
          { text: 'forum post', url: '/forum?cid=0' }
        ]
      }
    },
    {
      id: 'host-office-hours',
      category: 'Knowledge Sharing',
      activity: 'Host Office Hours',
      networkValue: 'Host office hours where you share your expertise with PL Network members',
      points: '100',
      frequency: 'Repeatable',
      verificationType: 'Auto',
      cta: 'confirm',
      popupContent: {
        title: 'Host Office Hours',
        overview: 'Host live sessions to share expertise, provide feedback, or collaborate with others in the network. These real-time connections not only share knowledge, but build stronger connections. Set yourself up to host office hours via your profile page.\n\nYou can host office hours through your PL Network profile. For meetings scheduled outside the Directory, add plaa-oh@plrs.xyz as an attendee to make the session eligible for automatic tracking.',
        networkBenefits: 'Makes member expertise accessible and creates lightweight paths for feedback, collaboration, and mentoring across the network.',
        rules: [
          'Sessions must either be scheduled through the Directory or include plaa-oh@plrs.xyz as an attendee on the calendar invitation',
          'Each session must run for at least 15 minutes',
          'The tracking email must be added to the calendar invitation before the session occurs',
          'Cancelled, declined, duplicated, or unverified sessions are not eligible'
        ],
        links: [
          { text: 'plaa-oh@plrs.xyz', url: 'mailto:plaa-oh@plrs.xyz' }
        ]
      }
    },
    {
      id: 'book-office-hours',
      category: 'Knowledge Sharing',
      activity: 'Book Office Hours',
      networkValue: 'Book office hours with a PL Network member to get advice, feedback, or explore collaboration',
      points: '100',
      frequency: 'Repeatable',
      verificationType: 'Auto',
      cta: 'confirm',
      popupContent: {
        title: 'Book Office Hours',
        overview: 'Book an office hours session with another member of the PL Network to seek advice, get feedback, or explore opportunities for collaboration. Connect directly with network experts through their PL Network profiles.\n\nYou can book office hours through the PL Network Directory. For meetings scheduled outside the Directory, add plaa-oh@plrs.xyz as an attendee to make the session eligible for automatic tracking.',
        networkBenefits: 'Encourages knowledge sharing, mentorship, and collaboration by connecting members with expertise across the network.',
        rules: [
          'Sessions must either be scheduled through the Directory or include plaa-oh@plrs.xyz as an attendee on the calendar invitation',
          'Each session must run for at least 15 minutes',
          'The tracking email must be added to the calendar invitation before the session occurs',
          'Cancelled, declined, duplicated, or unverified sessions are not eligible',
          'Points are awarded to the member booking and attending the session, not the host'
        ],
        links: [
          { text: 'plaa-oh@plrs.xyz', url: 'mailto:plaa-oh@plrs.xyz' }
        ]
      }
    },
    {
      id: 'comment-on-network-news',
      category: 'Knowledge Sharing',
      activity: 'Comment on News from the Network',
      networkValue: 'Upvote useful network news',
      points: '100',
      frequency: 'Repeatable',
      verificationType: 'Auto',
      cta: 'confirm',
      popupContent: {
        title: 'Comment on News from the Network',
        overview: 'Comment on useful, timely, or relevant news on the LabOS homepage to share your perspective and contribute to the network conversation.',
        networkBenefits: 'Introduces members to a LabOS feature while encouraging thoughtful discussion, knowledge sharing, and additional context around news that matters to the network.',
        rules: [
          'Comment on an existing news post on the LabOS homepage',
          'The comment must add relevant context, perspective, insight, or a thoughtful response to the news post',
          'The comment must remain visible when completion is checked',
          'Each post may qualify only once per participant',
          'Participants may earn points once per month for completing this activity'
        ]
      }
    },
    {
      id: 'submit-deal-debrief',
      category: 'Knowledge Sharing',
      activity: 'Submit a Deal Debrief',
      networkValue: 'Share the lessons from a VC, M&A, or other deal through the PL Deal Debrief app',
      points: '250',
      frequency: 'Repeatable',
      verificationType: 'Manual Review',
      cta: 'submit',
      audience: 'pl-infra-members',
      popupContent: {
        title: 'Submit a Deal Debrief',
        overview: 'Share the lesson, not just the deal. Submit a substantive debrief through the PL Deal Debrief app, drawing on your own perspective on a VC, M&A, or other deal. Explain what worked, what was challenging, and what others could learn (without disclosing confidential, privileged, or identifying deal information).',
        networkBenefits: 'Builds a repository of practical deal knowledge that can inform reports and advice for network companies and PL Infra, helping them improve deal processes and outcomes.',
        rules: [
          'Submit all deal information for this activity through the Deal Debrief app and complete all required fields, using generalized, non-confidential information.',
          'Provide substantive information: meaningful context, insights, and reusable lessons from your own perspective. Empty, generic, or copied answers do not qualify.',
          'The Legal Ops team will review all deal information before you collect the 250 points. Submission alone does not guarantee points.',
          'Multiple people may submit the same deal, provided each person supplies unique answers representing their own perspective on that deal.',
          `Do not submit confidential or privileged information about any deal. Follow the app's guidance: no names, exact figures, dates, non-public diligence reports, third-party term sheets, documents subject to NDAs, or confidential links in your deal narrative. Do not submit information you are not authorized to share; if you cannot answer safely, do not submit it.`,
          'Providing confidential or privileged deal information will result in immediate point forfeiture and potential removal from the activity program.'
        ],
        ctaLink: '/pl-infra/ai-apps/cmtdebct4000otb4fqp4vyim8',
        links: [
          { text: 'Deal Debrief app', url: '/pl-infra/ai-apps/cmtdebct4000otb4fqp4vyim8' }
        ],
        disclaimer: 'By submitting this activity, I warrant my submission is accurate, authorized, and breaches no confidentiality, securities, IP, or privacy obligation, and I release PL from all liability arising from it.'
      }
    },
    {
      id: 'network-introduction',
      category: 'Programs',
      activity: 'Make a Network Introduction',
      networkValue: 'Connect members or PL Network organizations',
      points: '500',
      frequency: 'Repeatable',
      verificationType: 'Submission',
      cta: 'submit',
      popupContent: {
        title: 'Make a Network Introduction',
        overview: 'Facilitate meaningful introductions between individuals or teams that may lead to collaboration or partnership.',
        networkBenefits: 'Creates new collaboration paths between members or teams that would otherwise stay disconnected.',
        rules: [
          'Must be a verified introduction between two parties with no prior contact'
        ]
      }
    },
    {
      id: 'high-value-connector',
      category: 'Programs',
      activity: 'High Value Connector',
      networkValue: 'Your introduction led to big things for the members or orgs you connected',
      points: '1000',
      frequency: 'Repeatable',
      verificationType: 'Submission',
      cta: 'submit',
      popupContent: {
        title: 'High Value Connector',
        overview: 'You not only connected people, but that connection added outsized value to the PL Network. These kinds of high-leverage introductions are why the PL Network exist and why we created the Alignment Asset.',
        networkBenefits: 'Incentivizes introductions that produce measurable outcomes such as advisors, partnerships, customers, or other network wins.',
        rules: [
          'Your connection led to a demonstrated, positive impact to the PL Network, like a new advisor, partnership, customer or other meaningful success',
          'Points are awarded based on the discretion of the PLAA WG'
        ]
      }
    },
    {
      id: 'respond-irl-gathering',
      category: 'Programs',
      activity: 'Attend an IRL Gathering',
      networkValue: 'RSVP to and attend a PL Network-related event',
      points: '100',
      frequency: 'Repeatable',
      verificationType: 'Auto',
      cta: 'confirm',
      popupContent: {
        title: 'Attend an IRL Gathering',
        overview: 'RSVP on the LabOS site to confirm your planned attendance at a PL Network-related event to help the organizers plan and strengthen the network.',
        networkBenefits: 'Improves event participation data and supports better planning for in-person network gatherings.',
        rules: [
          `Respond "I'm Going" on the relevant event page via IRL Gatherings before attending to qualify for points`,
          `Click I'm Going -> Select Event -> Role: Attendee, and ensure you are listed as an Attendee on the event page through your directory profile`,
          'Must attend to receive points'
        ],
        links: [
          { text: 'RSVP on the LabOS site', url: '/events/irl' }
        ]
      }
    },
    {
      id: 'give-excellent-feedback',
      category: 'Projects',
      activity: 'Give Excellent Feedback',
      networkValue: 'You went above and beyond in giving thoughtful and actionable feedback',
      points: '100',
      frequency: 'Repeatable',
      verificationType: 'Auto',
      cta: 'confirm',
      popupContent: {
        title: 'Give Excellent Feedback',
        overview: 'Your feedback — whether through a survey, the LabOS Feedback button, or another PLAA channel — went above and beyond. The insights you provided helped identify new issues or drive real improvements to the Alignment Asset program.',
        networkBenefits: 'Rewards feedback that identifies meaningful issues or improvement opportunities, not just completed forms.',
        rules: [
          'Your feedback created meaningful impact, as determined by the PLAA working group',
          'The PLAA working group sent you an email noting that your feedback made you eligible for these extra points'
        ]
      }
    },
    {
      id: 'complete-a-survey',
      category: 'Projects',
      activity: 'Complete a Survey',
      networkValue: `Complete a survey distributed by PLAA — whether from a PL Network member or PLAA itself — to help gather useful data`,
      points: '50',
      frequency: 'Repeatable',
      verificationType: 'Auto',
      cta: 'confirm',
      popupContent: {
        title: 'Complete a Survey',
        overview: 'Giving good feedback is one way we can help PL Network members improve their products, organization, research or mission. It might feel like a small thing, but data is valuable. This also includes surveys distributed directly by PLAA, which you can find in emails we send on an ad hoc basis.',
        networkBenefits: 'Helps member organizations gather useful data from outside their own teams, and gives PLAA a reliable feedback signal for improving the program, products, and participant experience.',
        rules: [
          'You completed a survey distributed by PLAA — either from a PL Network member or from the PLAA program itself',
          'If completing a PL Network member survey, you are not a member of the organization that submitted it for distribution',
          `Surveys must be completed before the end of the month in which they're sent`,
          'Bot or Spam responses may result in the forfeiture of points'
        ]
      }
    },
    {
      id: 'provide-product-testing-feedback',
      category: 'Projects',
      activity: 'Provide Product Testing + Feedback',
      networkValue: 'Test an approved product, tool, or workflow on a real task and share meaningful, actionable feedback',
      points: '100',
      frequency: 'Repeatable',
      verificationType: 'Manual Review',
      cta: 'confirm',
      audience: 'pl-infra-members',
      popupContent: {
        title: 'Provide Product Testing + Feedback',
        overview: `Test an approved product, tool, workflow, or process on a real work task and provide meaningful, actionable feedback on the experience.\n\nCurrent eligible requests for product testing:\n\n**Factorio Weekly Updates** — Submit your weekly update, then provide feedback using the LabOS feedback feature in the bottom-right corner of the screen. Collect an additional 100 points for sharing meaningful feedback on the broader UX and the features you would find most useful in your daily workflow. Feedback will be eligible for points through Saturday, October 31, 2026.\n\n[**Max**](https://os.pl.xyz/pl-infra/ai-apps/cmunhqj0l00hgsd4fl7e86h4g) — Get set up on Max, explore your initial Radar results, and complete the [Max First Impressions Survey](https://claude.ai/artifact/U11kcPTw19MN3ruXrgdHjB?sk=HkBoSTEdQyiG9wCWD_487g) to share feedback on ease of use and the relevance of the outputs. Collect 100 points for completing the test and survey. Feedback is eligible through Saturday, October 31, 2026.`,
        networkBenefits: 'Helps teams improve shared products and workflows through real-world use, identifying friction, missing capabilities, and opportunities to improve the user experience.',
        rules: [
          'Participate in an eligible product-testing request and complete the defined task',
          'On the designated feedback form/feature, describe what you tried, what happened, and provide at least one actionable observation',
          'Feedback may identify friction, bugs, missing capabilities, suggested UX/UI improvements, or something that worked well and should be retained',
          'Feedback must be meaningful and specific enough for the product owner to act on',
          'Attendance, account creation, routine use, demos, logins, praise alone, or general reactions do not qualify'
        ],
        links: [
          { text: 'Factorio Weekly Updates', url: '/pl-infra/ai-apps/cmst544z7008siz4g1d59fubr/weekly' }
        ]
      }
    },
    {
      id: 'bring-new-members',
      category: 'Projects',
      activity: 'Refer New Alignment Asset Participants',
      networkValue: 'Help grow the Alignment Asset program by referring new participants',
      points: '200',
      frequency: 'Repeatable',
      verificationType: 'Submission',
      cta: 'submit',
      popupContent: {
        title: 'Refer New Alignment Asset Participants',
        overview: `Refer eligible participants to join the Alignment Asset program. Reach out to plaa-wg@plrs.xyz with the person you wish to invite's name and email. The working group will review and share referral instructions with you and the person you referred. This is how we grow the Alignment Asset program and the PL Network at-large.`,
        networkBenefits: 'Supports responsible growth of the Alignment Asset participant base through trusted referrals.',
        rules: [
          'The person must complete any eligibility form (if not already eligible) The person you refer must complete all required onboarding tasks with Surus and the PLAA to qualify',
          'The person must also complete at least one activity in the first 60 days after onboarding'
        ],
        links: [
          { text: 'plaa-wg@plrs.xyz', url: 'mailto:plaa-wg@plrs.xyz' }
        ]
      }
    },
    {
      id: 'rank-supportive-members',
      category: 'Brand',
      activity: 'Top the Leaderboard',
      networkValue: 'Collect bonus points by finishing in the top 5 on the leaderboard in any snapshot period',
      points: '100',
      frequency: 'Repeatable',
      verificationType: 'Auto',
      cta: 'confirm',
      popupContent: {
        title: 'Top the Leaderboard',
        overview: `Finishing in the top five on the leaderboard isn't just a personal flex, it's a symbol of dedication to the PL Network and your efforts to make the network stronger.`,
        networkBenefits: 'Creates an additional visible incentive for sustained contribution during each snapshot period.',
        rules: [
          'At the close of each snapshot period, the top 5 users with the highest verified point totals receive the bonus',
          'Points are awarded only after all activity for the snapshot has been reviewed, finalized, and closed'
        ]
      }
    },
    {
      id: 'give-network-kudos',
      category: 'People/Talent',
      activity: 'Give a Network Kudos',
      networkValue: 'Give out 10–100 points! You decide who deserves the Kudos…',
      points: '0',
      frequency: 'Repeatable',
      verificationType: 'Auto',
      cta: 'confirm',
      popupContent: {
        title: 'Give a Network Kudos',
        overview: 'Give Kudos to recognize another network member for a helpful, meaningful, or positive contribution. Giving Kudos does not allow you to collect points, but the recipient may receive between 10 and 100 points based on the level of recognition awarded.',
        networkBenefits: 'Encourages peer recognition and helps surface valuable contributions that may not be captured through other activities.',
        rules: [
          'Kudos must be given through the official network Kudos feature.',
          'You cannot award Kudos to yourself.',
          'Kudos may be reviewed or removed if they appear duplicated, coordinated, or otherwise misused.'
        ],
        links: [
          { text: 'Kudos feature', url: '/alignment-asset/kudos' }
        ]
      }
    },

    // ─── Recurring ────────────────────────────────────────────────────────────
    {
      id: 'host-x-space',
      category: 'Brand',
      activity: 'Host or Co-Host an X Space',
      networkValue: `Increase the PL Network's visibility by hosting an X Space related to the PL Network`,
      points: '500',
      frequency: 'Recurring',
      verificationType: 'Submission',
      cta: 'submit',
      popupContent: {
        title: 'Host or Co-Host an X Space',
        overview: 'Host or co-host an approved X Space that highlights innovation, collaboration, or thought leadership across the network.',
        networkBenefits: 'Brings more external visibility to PL Network projects, ideas, and contributors.',
        rules: [
          'Proposals must be submitted and approved before scheduling',
          'X Space must be hosted on the official PL X account to ensure metrics can be verified',
          'At least 50 people must attend the X Space'
        ]
      }
    },
    {
      id: 'refer-team-member',
      category: 'People/Talent',
      activity: 'Refer a Potential Team Member to a PL Network Org',
      networkValue: 'Grow the network by referring someone you know for an open position at a PL Network company or org',
      points: '100',
      frequency: 'Recurring',
      verificationType: 'Submission',
      cta: 'submit',
      popupContent: {
        title: 'Refer a Potential Team Member to a PL Network Org',
        overview: 'Refer exceptional talent to the Protocol Labs network by submitting candidates through the designated form. Check out the PL Network Job Board to see open roles: /jobs.',
        networkBenefits: 'Expands the candidate pipeline for open roles across network companies and organizations.',
        rules: [
          'The referral must be for an open role at a PL Network Member organization or company',
          'The org can only be your own organzation or company, if you are not a member of the executive or founding team',
          'The referral must at least pass the screening interview stage'
        ],
        links: [
          { text: '/jobs', url: '/jobs' }
        ]
      }
    },
    {
      id: 'propose-incentivized-activity',
      category: 'Programs',
      activity: 'Propose a New Activity for the PLAA',
      networkValue: 'Design an activity for PLAA that will get participants to contribute to the network',
      points: '250',
      frequency: 'Recurring',
      verificationType: 'Submission',
      cta: 'submit',
      popupContent: {
        title: 'Propose a New Activity for the PLAA',
        overview: 'Propose a new Alignment Asset activity that solves a clear network challenge or unmet need.',
        networkBenefits: 'Sources activity ideas directly from participants and validates whether they create real engagement.',
        rules: [
          'The activity must be reviewed and approved by the PLAA working group',
          'At least five participants (other than you) must complete the activity'
        ]
      }
    },
    {
      id: 'help-organize-event',
      category: 'Programs',
      activity: 'Help Organize an Event',
      networkValue: 'Host or co-host an event that delivers real value to the Protocol Labs community',
      points: '1000',
      frequency: 'Recurring',
      verificationType: 'Auto',
      cta: 'confirm',
      popupContent: {
        title: 'Help Organize an Event',
        overview: 'Host or co-host a curated activity or event catered to Protocol Labs participants to aid with an array of topics, ranging from company formation, dedicated development, cross-network collaboration, etc. Events should be intentionally designed, independently run without direct PL, Inc. or Polaris, Inc. sponsorship in the form of direct compensation for time or labor, and focused on delivering real value to the Protocol Labs community.',
        networkBenefits: 'Encourages member-led events that deliver practical value without relying solely on central sponsorship.',
        rules: [
          'The event must be created and published in Luma and included on a live, discoverable calendar or listing',
          'You must add events@plrs.xyz as an Event Manager in Luma so registration and attendance data can be accessed',
          'Host must submit it to the PL events directory by adding it to /events/irl via https://irl.plnetwork.io/events/',
          'To qualify as a Host, your profile must be clearly listed under the Host section of the event',
          'At least 10 people not associated with your organization or company must attend'
        ],
        links: [
          { text: 'events@plrs.xyz', url: 'mailto:events@plrs.xyz' },
          { text: '/events/irl', url: '/events/irl' },
          { text: 'https://irl.plnetwork.io/events/', url: 'https://irl.plnetwork.io/events/' }
        ]
      }
    },
    {
      id: 'share-ai-resource',
      category: 'Network Tooling',
      activity: 'Share an AI Resource or Tool You Built',
      networkValue: `Share a cool AI resource or tool you built on the LabOS forum that's useful to other PL Network members`,
      points: '250',
      frequency: 'Recurring',
      verificationType: 'Submission',
      cta: 'confirm',
      popupContent: {
        title: 'Share an AI Resource or Tool You Built',
        overview: 'Share a reusable AI resource that helps other network members learn from, adapt, or adopt a useful AI-enabled workflow. Eligible resources may include AI tools, automations, prompt libraries, .md guides, playbooks, templates, scripts, or examples of how AI is being used to improve team operations, product work, research, community building, or other network-relevant work. Strong posts should explain the problem the resource solves, highlight relevant use cases, and invite feedback, questions, improvements, adaptations, or examples from other members who may want to apply it in their own work.',
        networkBenefits: 'Helps teams discover reusable AI workflows, tools, templates, and examples from peers.',
        rules: [
          'You must make a forum post of at least 100 words about the tool or resource on the LabOS forum',
          'The resource must be hosted on a public Github repository',
          'The GitHub resource must include clear documentation or usage instructions'
        ]
      }
    },
    {
      id: 'build-ai-app',
      category: 'Network Tooling',
      activity: 'Build an AI App',
      networkValue: 'Deploy an app on LabOS',
      points: '1000',
      frequency: 'Recurring',
      isSunset: true,
      audience: 'pl-infra-members',
      verificationType: 'Manual Review',
      cta: 'confirm',
      popupContent: {
        title: 'Build an AI App',
        overview: 'Create and deploy a functional AI application using LabOS infrastructure that provides practical value to PL Infra users.',
        networkBenefits: 'Expands useful network tooling while driving adoption and feedback for LabOS infrastructure.',
        rules: [
          'Available to PL Infra members only. Other participants may submit qualifying tools through the Share an AI Resource or Tool You Built activity.',
          'Create and deploy a functional AI application through LabOS via /pl-infra/ai-apps.',
          'The application must be useful to the average PL Infra user.',
          'Include basic documentation and identify any collaborators.',
          'Applications are reviewed by the PLAA Working Group before points are awarded.'
        ],
        links: [
          { text: '/pl-infra/ai-apps', url: '/pl-infra/ai-apps' }
        ]
      }
    },
    {
      id: 'contribute-reusable-prompt-or-skill',
      category: 'Network Tooling',
      activity: 'Contribute a Reusable Prompt or Skill',
      networkValue: 'Contribute a reusable prompt, AI resource, or skill to the PLaybook',
      points: '350',
      frequency: 'Recurring',
      verificationType: 'Manual Review',
      cta: 'confirm',
      audience: 'pl-infra-members',
      popupContent: {
        title: 'Contribute a Reusable Prompt or Skill',
        overview: 'Contribute a reusable resource, prompt or AI skill to the PLaybook for PL Infra users. Receive 100 points for an approved resource or prompt and also 250 points for an approved skill, up to 350 points total.',
        networkBenefits: 'Builds a library of useful, reusable AI resources while encouraging PL Infra members to share proven knowledge resources, workflows and tools with the broader network.',
        rules: [
          '100 points are awarded for an approved reusable resource or prompt; 250 points are awarded for an approved skill (up to 350 points total)',
          'Available to current PLAA participants with verified PL Infra membership',
          'Submit up to one prompt or AI-related resource and one AI skill',
          'Contributions must be publication-verified, useful, reproducible, safe, and non-duplicative'
        ],
        links: [
          { text: 'PLaybook', url: '/pl-infra/ai-apps/cmuqgrc1j043ov34fw48iu5ou' }
        ]
      }
    },

    // ─── One-Time ─────────────────────────────────────────────────────────────
    {
      id: 'update-directory-profile',
      category: 'Network Tooling',
      activity: 'Update Your PL Directory Profile',
      networkValue: 'Bring your PL Network profile up to date so members can find you, reach you, and see what you work on',
      points: '250',
      frequency: 'One-Time',
      verificationType: 'Auto',
      cta: 'confirm',
      popupContent: {
        title: 'Update Your PL Directory Profile',
        overview: 'Update your PL Directory profile so other members can see what you work on and how to reach you. This activity has restarted with new requirements, so everyone can collect points for it, including members who completed the previous version.\n\nOpen your profile in the Directory and complete each section: Contact Details, Your CV, Project Contributions, and Office Hours.',
        networkBenefits: 'Makes members easier to find and contact, shows the projects people have contributed to, and gives the network better context for introductions, hiring, and collaboration.',
        rules: [
          'Your profile must include your name, email, role, LinkedIn, and Telegram',
          'In Contact Details, turn on **Show contact details to PL network members** (contact details are never displayed publicly)',
          'Upload your CV in the **Your CV** section (PDF, up to 5MB)',
          'List at least one **Project Contribution** with the project name, your role, and a short summary of your impact',
          'Set up office hours and have a live office hours link',
          'Optional but encouraged: add your **GitHub** handle in Contact Details. It is not required for this activity, but it helps verify other activities that use GitHub'
        ]
      }
    },
    {
      id: 'share-compensation-data',
      category: 'People/Talent',
      activity: 'Share Compensation Data',
      networkValue: 'Share your compensation data with our talent team',
      points: '50',
      frequency: 'One-Time',
      verificationType: 'Submission',
      cta: 'submit',
      popupContent: {
        title: 'Share Compensation Data',
        overview: 'Share your compensation data to support network-wide compensation transparency, benchmarking, and pay-equity insights. Submit your information securely using the designated form. All information will be anonymized.',
        networkBenefits: 'Improves compensation benchmarking and pay-equity insight while keeping submitted information anonymized.',
        rules: [
          'You must complete the submission form',
          'You may only submit compensation data up to 2 times per year for points'
        ]
      }
    },
    {
      id: 'subscribe-pl-newsletter',
      category: 'Brand',
      activity: 'Subscribe to PL Newsletter',
      networkValue: 'Subscribe to receive PL Network updates',
      points: '50',
      frequency: 'One-Time',
      verificationType: 'Auto',
      cta: 'confirm',
      popupContent: {
        title: 'Subscribe to PL Newsletter',
        overview: 'Subscribe to the PL Newsletter to receive network news, opportunities, events, and other important updates directly in your inbox.',
        networkBenefits: 'Keeps more members informed and connected to opportunities across the PL Network.',
        rules: [
          'Turn on the Subscribe to PL Newsletter toggle in Directory via /settings/email',
          'The toggle must remain enabled when the subscription is verified',
          'Points are awarded after Marketing confirms the subscription',
          'This activity may only be completed once'
        ],
        links: [
          { text: '/settings/email', url: '/settings/email' }
        ]
      }
    }
  ]
};
